import React, { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  es: {
    // Common
    loading: 'Cargando...',
    save: 'Guardar',
    cancel: 'Cancelar',
    delete: 'Eliminar',
    edit: 'Editar',
    create: 'Crear',
    search: 'Buscar',
    back: 'Volver',
    confirm: 'Confirmar',
    close: 'Cerrar',
    or: 'o',

    // Navigation
    nav: {
      home: 'Inicio',
      dashboard: 'Panel',
      recipes: 'Recetas',
      menus: 'Menús',
      training: 'Entrenamiento',
      history: 'Historial',
      settings: 'Ajustes',
      admin: 'Admin',
      logout: 'Cerrar sesión',
      login: 'Iniciar sesión',
      pricing: 'Precios',
    },

    training: {
      title: 'Entrenamiento',
      subtitle: 'Tu perfil deportivo y tus objetivos nutricionales personalizados',
      exerciseLibrary: 'Ver biblioteca de ejercicios',
      myPlans: 'Mis planes de entrenamiento',
      nutritionPlans: 'Planes nutricionales',
      progress: 'Mi evolución',
      recordRun: 'Grabar carrera',
      premiumOnly: 'El módulo de entrenamiento está disponible solo para usuarios Premium.',
      upgrade: 'Mejorar a Premium',
      select: 'Selecciona',
      save: 'Guardar perfil',
      saved: 'Perfil guardado',
      errors: { required: 'Completa los datos físicos, el objetivo y el nivel de actividad' },
      sections: {
        physical: 'Datos físicos',
        goal: 'Objetivo y actividad',
        health: 'Salud',
        sport: 'Deporte y disponibilidad',
      },
      fields: {
        sex: 'Sexo', age: 'Edad', height: 'Altura (cm)', weight: 'Peso (kg)',
        goal: 'Objetivo', activity: 'Nivel de actividad', experience: 'Experiencia',
        injuries: 'Lesiones o zonas a evitar',
        injuriesPlaceholder: 'Ej: molestia en rodilla derecha, evitar impacto...',
        sports: 'Deportes que puedes o quieres hacer',
        daysPerWeek: 'Días por semana', sessionMinutes: 'Minutos por sesión',
        equipment: 'Material disponible',
        equipmentPlaceholder: 'Ej: mancuernas, banda elástica, barra...',
        availability: 'Notas de disponibilidad',
        availabilityPlaceholder: 'Ej: una semana 4 días y la siguiente 3; entreno por las mañanas...',
      },
      sex: { male: 'Hombre', female: 'Mujer' },
      goal: { fat_loss: 'Perder grasa', definition: 'Definición', muscle_gain: 'Ganar músculo', performance: 'Rendimiento', maintenance: 'Mantenimiento' },
      activity: { sedentary: 'Sedentario', light: 'Ligero', moderate: 'Moderado', active: 'Activo', very_active: 'Muy activo' },
      experience: { beginner: 'Principiante', intermediate: 'Intermedio', advanced: 'Avanzado' },
      sport: { walking: 'Caminar', running: 'Correr', swimming: 'Nadar', strength_home: 'Fuerza en casa', strength_gym: 'Fuerza en gym', cycling: 'Ciclismo' },
      parq: {
        intro: 'Marca las que te apliquen (cuestionario de salud PAR-Q):',
        q1: '¿Te ha dicho un médico que tienes un problema de corazón?',
        q2: '¿Sientes dolor en el pecho al hacer actividad física?',
        q3: '¿Has tenido mareos o pérdidas de conocimiento?',
        q4: '¿Tienes algún problema óseo o articular que pueda empeorar con el ejercicio?',
        q5: '¿Tomas medicación recetada para la tensión o el corazón?',
        q6: '¿Conoces alguna otra razón por la que no debas hacer ejercicio?',
        q7: '¿Estás embarazada o en posparto reciente?',
      },
      targets: {
        title: 'Tus objetivos',
        bmr: 'BMR', tdee: 'TDEE',
        trainingDay: 'Día de entreno', restDay: 'Día de descanso',
        empty: 'Completa y guarda tu perfil para ver tus objetivos.',
        disclaimer: 'Estimación orientativa (fórmula Mifflin-St Jeor). No sustituye el consejo de un profesional médico o nutricionista.',
      },
    },

    // Landing
    landing: {
      hero: {
        title: 'Planifica tu semana de comidas sin estrés',
        subtitle: 'Genera menús semanales automáticamente, gestiona tus recetas favoritas y comparte con tu familia.',
        cta: 'Comenzar gratis',
        secondary: 'Ver precios',
      },
      features: {
        title: 'Todo lo que necesitas',
        autoGenerate: {
          title: 'Generación automática de menús',
          desc: 'Crea menús semanales con un clic respetando tus preferencias.',
        },
        recipes: {
          title: 'Tu biblioteca de recetas',
          desc: 'Guarda y organiza todas tus recetas favoritas.',
        },
        share: {
          title: 'Comparte fácilmente',
          desc: 'Genera imágenes para compartir tu menú por WhatsApp.',
        },
        calories: {
          title: 'Cálculo de calorías',
          desc: 'Estimación automática de calorías con IA.',
        },
      },
      cta: {
        ready: '¿Listo para simplificar tu semana?',
        start: 'Empieza a planificar hoy',
      },
    },

    // Auth
    auth: {
      loginTitle: 'Bienvenido de nuevo',
      loginSubtitle: 'Inicia sesión para acceder a tu cuenta',
      continueWithGoogle: 'Continuar con Google',
      processing: 'Procesando...',
      verificationCodeSent: 'Código de verificación enviado',
    },

    // Dashboard
    dashboard: {
      title: 'Tu semana',
      today: 'Hoy',
      noMenus: '¡No tienes menús aún!',
      noMenusHint: 'Crea tu primer menú semanal',
      generateMenu: 'Generar menú',
      viewAll: 'Ver todos',
      breakfast: 'Desayuno',
      lunch: 'Almuerzo',
      dinnerMain: 'Cena (principal)',
      dinnerSecondary: 'Cena (acompañamiento)',
      snack: 'Snack',
      dinnerMainShort: 'Cena Ppal',
      dinnerSecondaryShort: 'Cena Acomp.',
      kcal: 'kcal',
      weekTotal: 'Total semanal',
    },

    // Days
    days: {
      monday: 'Lunes',
      tuesday: 'Martes',
      wednesday: 'Miércoles',
      thursday: 'Jueves',
      friday: 'Viernes',
      saturday: 'Sábado',
      sunday: 'Domingo',
    },
    daysShort: {
      monday: 'Lun',
      tuesday: 'Mar',
      wednesday: 'Mié',
      thursday: 'Jue',
      friday: 'Vie',
      saturday: 'Sáb',
      sunday: 'Dom',
    },

    // Recipes
    recipes: {
      title: 'Mis recetas',
      add: 'Nueva receta',
      empty: '¡No tienes recetas todavía!',
      emptyHint: 'Crea tu primera receta para empezar',
      categories: {
        all: 'Todas',
        breakfast: 'Desayuno',
        lunch: 'Almuerzo',
        dinner_main: 'Cena (principal)',
        dinner_secondary: 'Cena (secundario)',
        snack: 'Snack',
      },
      nutrition: {
        title: 'Información nutricional (por ración)',
        calories: 'Calorías',
        protein: 'Proteínas',
        carbs: 'Carbohidratos',
        fat: 'Grasas',
        fiber: 'Fibra',
        estimate: 'Estimar con IA',
        estimating: 'Estimando...',
        approximate: 'Aproximado',
        premiumOnly: 'La estimación con IA es solo para Premium',
      },
      fromIngredients: {
        button: 'Generar desde ingredientes',
        title: 'Generar receta desde ingredientes',
        subtitle: 'Dime qué tienes en casa y la IA te propondrá platos',
        ingredientsLabel: 'Ingredientes disponibles',
        ingredientPlaceholder: 'Ej: macarrones',
        addIngredient: 'Añadir ingrediente',
        suggest: 'Ver ideas de platos',
        suggesting: 'Buscando ideas...',
        suggestionsTitle: 'Elige un plato',
        uses: 'Usa',
        missing: 'Te faltaría',
        generate: 'Crear esta receta',
        generating: 'Creando receta...',
        premiumOnly: 'Función solo para usuarios Premium',
        noIngredients: 'Añade al menos un ingrediente',
        error: 'No se pudo generar. Inténtalo de nuevo.',
      },
      form: {
        name: 'Nombre de la receta',
        namePlaceholder: 'Ej: Paella valenciana',
        description: 'Descripción',
        descriptionPlaceholder: 'Describe brevemente la receta...',
        category: 'Categoría',
        selectCategory: 'Selecciona categoría',
        maxRepetitions: 'Máx. repeticiones/semana',
        prepTime: 'Tiempo de preparación (min)',
        image: 'Imagen',
        imagePlaceholder: 'https://...',
        ingredients: 'Ingredientes',
        addIngredient: 'Añadir ingrediente',
        ingredientName: 'Ingrediente',
        ingredientQuantity: 'Cantidad',
        generationConstraint: 'Restricción de generación',
        maxRepetitionsType: 'Máximo por semana',
        specificDaysType: 'Días específicos',
        allowedDays: 'Días permitidos',
        creating: 'Creando...',
        updating: 'Actualizando...',
      },
      deleteConfirm: '¿Estás seguro de eliminar esta receta?',
      calories: 'calorías',
      approxCalories: '(aprox.)',
      prepTime: 'min',
    },

    // Menu
    menu: {
      title: 'Menú semanal',
      generate: 'Generar menú',
      generating: 'Generando...',
      save: 'Guardar menú',
      publish: 'Publicar imagen',
      edit: 'Editar',
      history: 'Historial',
      form: {
        title: 'Título del menú',
        titlePlaceholder: 'Ej: Semana del 20 enero',
        weekStart: 'Inicio de semana',
      },
      empty: 'Sin asignar',
      clickToChange: 'Clic para cambiar',
      selectRecipe: 'Seleccionar receta',
      noRecipesForSlot: 'No hay recetas disponibles para este tipo',
      published: '¡Imagen generada!',
      publishedHint: 'Descarga lista para compartir por WhatsApp',
      download: 'Descargar imagen',
    },

    // History
    history: {
      title: 'Historial de menús',
      empty: 'No tienes menús guardados',
      copy: 'Copiar como base',
      view: 'Ver menú',
      created: 'Creado',
    },

    // Settings
    settings: {
      title: 'Ajustes',
      profile: 'Perfil',
      language: 'Idioma',
      subscription: 'Suscripción',
      currentPlan: 'Plan actual',
      free: 'Gratuito',
      premium: 'Premium',
      upgrade: 'Mejorar a Premium',
      unlimited: 'Acceso ilimitado',
      thanksPremium: '¡Gracias por ser Premium!',
      limits: {
        recipes: 'Recetas',
        menus: 'Menús guardados',
        of: 'de',
        unlimited: 'ilimitado',
      },
      security: 'Seguridad',
      currentPassword: 'Contraseña actual',
      newPassword: 'Nueva contraseña',
      confirmPassword: 'Confirmar contraseña',
      changePassword: 'Cambiar contraseña',
      passwordChanged: 'Contraseña actualizada correctamente',
      passwordMismatch: 'Las contraseñas no coinciden',
      passwordTooShort: 'La contraseña debe tener al menos 6 caracteres',
      passwordWrong: 'La contraseña actual es incorrecta',
      manageSubscription: 'Gestionar suscripción',
      cancelSubscription: 'Cancelar suscripción',
      nextBilling: 'Próxima facturación',
      amount: 'Importe',
      status: 'Estado',
      active: 'Activo',
      canceled: 'Cancelado (finaliza al terminar el periodo)',
      past_due: 'Pago pendiente',
      canceling_at_period_end: 'Se cancelará el',
      renew: 'Reactivar suscripción',
    },

    // Admin
    admin: {
      title: 'Panel de administración',
      users: 'Usuarios',
      config: 'Configuración',
      stats: 'Estadísticas',
      totalUsers: 'Total usuarios',
      premiumUsers: 'Usuarios premium',
      freeUsers: 'Usuarios gratuitos',
      totalRecipes: 'Total recetas',
      totalMenus: 'Total menús',
      configOptions: {
        calorieEnabled: 'Cálculo de calorías habilitado',
        freeMaxRecipes: 'Máx. recetas (plan gratuito)',
        freeMaxMenus: 'Máx. menús (plan gratuito)',
      },
      deleteUser: 'Eliminar usuario',
      deleteUserConfirm: '¿Eliminar este usuario y todos sus datos?',
      makeAdmin: 'Hacer admin',
      removeAdmin: 'Quitar admin',
    },

    // Pricing
    pricing: {
      title: 'Planes simples y transparentes',
      subtitle: 'Elige el plan que mejor se adapte a tus necesidades',
      free: {
        title: 'Gratuito',
        price: '£0',
        period: 'para siempre',
        features: [
          '{recipes} recetas',
          '{menus} menús guardados',
          'Generación automática de menús',
        ],
        cta: 'Comenzar gratis',
      },
      pro: {
        title: 'Pro',
        monthly: '£5.99',
        yearly: '£59.99',
        monthlyPeriod: '/mes',
        yearlyPeriod: '/año',
        features: [
          'Recetas ilimitadas',
          'Menús ilimitados',
          'Generación automática de menús',
          'Soporte estándar',
        ],
        cta: 'Elegir Pro',
      },
      premium: {
        title: 'Premium',
        monthly: '£9.99',
        yearly: '£39.99',
        monthlyPeriod: '/mes',
        yearlyPeriod: '/año',
        yearlySave: 'Ahorra 66%',
        features: [
          'Todo lo de Pro',
          'Lista de la compra',
          'Cálculo de calorías con IA',
          'Soporte prioritario',
          'Añade recetas con enlace (importa con IA)',
          'Añade recetas con audio (sube o graba)',
          'Añade recetas con foto (sube o fotografía)',
        ],
        videoImportFeature: 'Añade recetas desde vídeos de YouTube',
        aiImagesFeature: 'Imagen generada con IA para cada receta',
        cta: 'Suscribirse a Premium',
      },
    },

    // Contact
    contact: {
      title: 'Contacto y Soporte',
      subtitle: '¿Tienes alguna duda, sugerencia o incidencia? Cuéntanos y te ayudaremos lo antes posible.',
      form: {
        subject: 'Asunto',
        subjectPlaceholder: 'Resumen de tu consulta',
        category: 'Categoría',
        message: 'Mensaje',
        messagePlaceholder: 'Escribe aquí los detalles...',
        send: 'Enviar mensaje',
        sending: 'Enviando...',
        categories: {
          general: 'Consulta general',
          bug: 'Reportar un error',
          suggestion: 'Sugerencia',
          billing: 'Facturación / Pagos',
        },
      },
      success: '¡Mensaje enviado!',
      successDetail: 'Hemos recibido tu consulta y te responderemos pronto a tu email.',
      supportText: '¿Necesitas ayuda personalizada?',
    },

    // Errors
    errors: {
      generic: 'Ha ocurrido un error',
      paymentFailed: 'No se ha podido procesar el pago. Por favor, inténtalo de nuevo o contacta con soporte.',
      notFound: 'No encontrado',
      unauthorized: 'No autorizado',
      limitReached: 'Has alcanzado el límite de tu plan',
      noRecipes: 'Necesitas crear recetas primero',
      rateLimit: 'Demasiados intentos. Por motivos de seguridad, tu acceso ha sido temporalmente limitado. Por favor, inténtalo de nuevo en unos minutos.',
      unverified: 'Tu cuenta no está verificada. Por favor, revisa tu email.',
      codeSent: 'Código enviado correctamente',
      resendCode: 'Reenviar código',
    },

    // Success
    success: {
      saved: 'Guardado correctamente',
      deleted: 'Eliminado correctamente',
      copied: 'Copiado correctamente',
    },
  },

  en: {
    // Common
    loading: 'Loading...',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    create: 'Create',
    search: 'Search',
    back: 'Back',
    confirm: 'Confirm',
    close: 'Close',
    or: 'or',

    // Navigation
    nav: {
      home: 'Home',
      dashboard: 'Dashboard',
      recipes: 'Recipes',
      menus: 'Menus',
      training: 'Training',
      history: 'History',
      settings: 'Settings',
      admin: 'Admin',
      logout: 'Log out',
      login: 'Log in',
      pricing: 'Pricing',
    },

    training: {
      title: 'Training',
      subtitle: 'Your athlete profile and personalised nutrition targets',
      exerciseLibrary: 'View exercise library',
      myPlans: 'My training plans',
      nutritionPlans: 'Nutrition plans',
      progress: 'My progress',
      recordRun: 'Record run',
      premiumOnly: 'The training module is available for Premium users only.',
      upgrade: 'Upgrade to Premium',
      select: 'Select',
      save: 'Save profile',
      saved: 'Profile saved',
      errors: { required: 'Fill in physical data, goal and activity level' },
      sections: {
        physical: 'Physical data',
        goal: 'Goal & activity',
        health: 'Health',
        sport: 'Sport & availability',
      },
      fields: {
        sex: 'Sex', age: 'Age', height: 'Height (cm)', weight: 'Weight (kg)',
        goal: 'Goal', activity: 'Activity level', experience: 'Experience',
        injuries: 'Injuries or areas to avoid',
        injuriesPlaceholder: 'E.g.: right knee discomfort, avoid impact...',
        sports: 'Sports you can or want to do',
        daysPerWeek: 'Days per week', sessionMinutes: 'Minutes per session',
        equipment: 'Available equipment',
        equipmentPlaceholder: 'E.g.: dumbbells, resistance band, barbell...',
        availability: 'Availability notes',
        availabilityPlaceholder: 'E.g.: 4 days one week and 3 the next; I train in the mornings...',
      },
      sex: { male: 'Male', female: 'Female' },
      goal: { fat_loss: 'Fat loss', definition: 'Definition', muscle_gain: 'Muscle gain', performance: 'Performance', maintenance: 'Maintenance' },
      activity: { sedentary: 'Sedentary', light: 'Light', moderate: 'Moderate', active: 'Active', very_active: 'Very active' },
      experience: { beginner: 'Beginner', intermediate: 'Intermediate', advanced: 'Advanced' },
      sport: { walking: 'Walking', running: 'Running', swimming: 'Swimming', strength_home: 'Strength at home', strength_gym: 'Strength at gym', cycling: 'Cycling' },
      parq: {
        intro: 'Tick the ones that apply to you (PAR-Q health questionnaire):',
        q1: 'Has a doctor ever told you that you have a heart condition?',
        q2: 'Do you feel chest pain during physical activity?',
        q3: 'Have you had dizziness or loss of consciousness?',
        q4: 'Do you have a bone or joint problem that could be worsened by exercise?',
        q5: 'Are you on prescribed medication for blood pressure or your heart?',
        q6: 'Do you know any other reason why you should not exercise?',
        q7: 'Are you pregnant or recently postpartum?',
      },
      targets: {
        title: 'Your targets',
        bmr: 'BMR', tdee: 'TDEE',
        trainingDay: 'Training day', restDay: 'Rest day',
        empty: 'Complete and save your profile to see your targets.',
        disclaimer: 'Indicative estimate (Mifflin-St Jeor formula). Not a substitute for advice from a medical or nutrition professional.',
      },
    },

    // Landing
    landing: {
      hero: {
        title: 'Plan your weekly meals stress-free',
        subtitle: 'Generate weekly menus automatically, manage your favorite recipes and share with your family.',
        cta: 'Start free',
        secondary: 'View pricing',
      },
      features: {
        title: 'Everything you need',
        autoGenerate: {
          title: 'Automatic menu generation',
          desc: 'Create weekly menus with one click respecting your preferences.',
        },
        recipes: {
          title: 'Your recipe library',
          desc: 'Save and organize all your favorite recipes.',
        },
        share: {
          title: 'Easy sharing',
          desc: 'Generate images to share your menu via WhatsApp.',
        },
        calories: {
          title: 'Calorie calculation',
          desc: 'Automatic AI-powered calorie estimation.',
        },
      },
      cta: {
        ready: 'Ready to simplify your week?',
        start: 'Start planning today',
      },
    },

    // Auth
    auth: {
      loginTitle: 'Welcome back',
      loginSubtitle: 'Sign in to access your account',
      continueWithGoogle: 'Continue with Google',
      processing: 'Processing...',
      verificationCodeSent: 'Verification code sent',
    },

    // Dashboard
    dashboard: {
      title: 'Your week',
      today: 'Today',
      noMenus: 'No menus yet!',
      noMenusHint: 'Create your first weekly menu',
      generateMenu: 'Generate menu',
      viewAll: 'View all',
      breakfast: 'Breakfast',
      lunch: 'Lunch',
      dinnerMain: 'Dinner (main)',
      dinnerSecondary: 'Dinner (side)',
      snack: 'Snack',
      dinnerMainShort: 'Dinner Main',
      dinnerSecondaryShort: 'Dinner Side',
      kcal: 'kcal',
      weekTotal: 'Weekly total',
    },

    // Days
    days: {
      monday: 'Monday',
      tuesday: 'Tuesday',
      wednesday: 'Wednesday',
      thursday: 'Thursday',
      friday: 'Friday',
      saturday: 'Saturday',
      sunday: 'Sunday',
    },
    daysShort: {
      monday: 'Mon',
      tuesday: 'Tue',
      wednesday: 'Wed',
      thursday: 'Thu',
      friday: 'Fri',
      saturday: 'Sat',
      sunday: 'Sun',
    },

    // Recipes
    recipes: {
      title: 'My recipes',
      add: 'New recipe',
      empty: 'No recipes yet!',
      emptyHint: 'Create your first recipe to get started',
      categories: {
        all: 'All',
        breakfast: 'Breakfast',
        lunch: 'Lunch',
        dinner_main: 'Dinner (main)',
        dinner_secondary: 'Dinner (side)',
        snack: 'Snack',
      },
      nutrition: {
        title: 'Nutrition facts (per serving)',
        calories: 'Calories',
        protein: 'Protein',
        carbs: 'Carbs',
        fat: 'Fat',
        fiber: 'Fiber',
        estimate: 'Estimate with AI',
        estimating: 'Estimating...',
        approximate: 'Approximate',
        premiumOnly: 'AI estimation is Premium only',
      },
      fromIngredients: {
        button: 'Generate from ingredients',
        title: 'Generate recipe from ingredients',
        subtitle: 'Tell me what you have at home and AI will suggest dishes',
        ingredientsLabel: 'Available ingredients',
        ingredientPlaceholder: 'E.g.: pasta',
        addIngredient: 'Add ingredient',
        suggest: 'See dish ideas',
        suggesting: 'Finding ideas...',
        suggestionsTitle: 'Pick a dish',
        uses: 'Uses',
        missing: 'You would need',
        generate: 'Create this recipe',
        generating: 'Creating recipe...',
        premiumOnly: 'Premium users only',
        noIngredients: 'Add at least one ingredient',
        error: 'Could not generate. Try again.',
      },
      form: {
        name: 'Recipe name',
        namePlaceholder: 'E.g.: Grilled chicken salad',
        description: 'Description',
        descriptionPlaceholder: 'Briefly describe the recipe...',
        category: 'Category',
        selectCategory: 'Select category',
        maxRepetitions: 'Max repetitions/week',
        prepTime: 'Prep time (min)',
        image: 'Image',
        imagePlaceholder: 'https://...',
        ingredients: 'Ingredients',
        addIngredient: 'Add ingredient',
        ingredientName: 'Ingredient',
        ingredientQuantity: 'Quantity',
        generationConstraint: 'Generation constraint',
        maxRepetitionsType: 'Maximum per week',
        specificDaysType: 'Specific days',
        allowedDays: 'Allowed days',
        creating: 'Creating...',
        updating: 'Updating...',
      },
      deleteConfirm: 'Are you sure you want to delete this recipe?',
      calories: 'calories',
      approxCalories: '(approx.)',
      prepTime: 'min',
    },

    // Menu
    menu: {
      title: 'Weekly menu',
      generate: 'Generate menu',
      generating: 'Generating...',
      save: 'Save menu',
      publish: 'Publish image',
      edit: 'Edit',
      history: 'History',
      form: {
        title: 'Menu title',
        titlePlaceholder: 'E.g.: Week of January 20',
        weekStart: 'Week start',
      },
      empty: 'Not assigned',
      clickToChange: 'Click to change',
      selectRecipe: 'Select recipe',
      noRecipesForSlot: 'No recipes available for this type',
      published: 'Image generated!',
      publishedHint: 'Ready to download and share via WhatsApp',
      download: 'Download image',
    },

    // History
    history: {
      title: 'Menu history',
      empty: 'No saved menus',
      copy: 'Copy as base',
      view: 'View menu',
      created: 'Created',
    },

    // Settings
    settings: {
      title: 'Settings',
      profile: 'Profile',
      language: 'Language',
      subscription: 'Subscription',
      currentPlan: 'Current plan',
      free: 'Free',
      premium: 'Premium',
      upgrade: 'Upgrade to Premium',
      unlimited: 'Unlimited access',
      thanksPremium: 'Thanks for being Premium!',
      limits: {
        recipes: 'Recipes',
        menus: 'Saved menus',
        of: 'of',
        unlimited: 'unlimited',
      },
      security: 'Security',
      currentPassword: 'Current password',
      newPassword: 'New password',
      confirmPassword: 'Confirm password',
      changePassword: 'Change password',
      passwordChanged: 'Password changed successfully',
      passwordMismatch: 'Passwords don\'t match',
      passwordTooShort: 'Password must be at least 6 characters',
      passwordWrong: 'Current password is incorrect',
      manageSubscription: 'Manage subscription',
      cancelSubscription: 'Cancel subscription',
      nextBilling: 'Next billing',
      amount: 'Amount',
      status: 'Status',
      active: 'Active',
      canceled: 'Canceled (ends at period end)',
      past_due: 'Payment due',
      canceling_at_period_end: 'Will cancel on',
      renew: 'Reactivate subscription',
    },

    // Admin
    admin: {
      title: 'Admin panel',
      users: 'Users',
      config: 'Configuration',
      stats: 'Statistics',
      totalUsers: 'Total users',
      premiumUsers: 'Premium users',
      freeUsers: 'Free users',
      totalRecipes: 'Total recipes',
      totalMenus: 'Total menus',
      configOptions: {
        calorieEnabled: 'Calorie calculation enabled',
        freeMaxRecipes: 'Max recipes (free plan)',
        freeMaxMenus: 'Max menus (free plan)',
      },
      deleteUser: 'Delete user',
      deleteUserConfirm: 'Delete this user and all their data?',
      makeAdmin: 'Make admin',
      removeAdmin: 'Remove admin',
    },

    // Pricing
    pricing: {
      title: 'Simple and transparent pricing',
      subtitle: 'Choose the plan that best fits your needs',
      free: {
        title: 'Free',
        price: '£0',
        period: 'forever',
        features: [
          '{recipes} recipes',
          '{menus} saved menus',
          'Automatic menu generation',
        ],
        cta: 'Start free',
      },
      pro: {
        title: 'Pro',
        monthly: '£5.99',
        yearly: '£59.99',
        monthlyPeriod: '/month',
        yearlyPeriod: '/year',
        features: [
          'Unlimited recipes',
          'Unlimited menus',
          'Automatic menu generation',
          'Standard support',
        ],
        cta: 'Choose Pro',
      },
      premium: {
        title: 'Premium',
        monthly: '£9.99',
        yearly: '£39.99',
        monthlyPeriod: '/month',
        yearlyPeriod: '/year',
        yearlySave: 'Save 66%',
        features: [
          'Everything in Pro',
          'Shopping list',
          'AI calorie calculation',
          'Priority support',
          'Add recipes via link (AI import)',
          'Add recipes via audio (upload or record)',
          'Add recipes via photo (upload or capture)',
        ],
        videoImportFeature: 'Add recipes from YouTube videos',
        aiImagesFeature: 'AI-generated image for each recipe',
        cta: 'Subscribe to Premium',
      },
    },

    // Contact
    contact: {
      title: 'Contact & Support',
      subtitle: 'Have a question, suggestion, or issue? Let us know and we will help you as soon as possible.',
      form: {
        subject: 'Subject',
        subjectPlaceholder: 'Summary of your inquiry',
        category: 'Category',
        message: 'Message',
        messagePlaceholder: 'Write the details here...',
        send: 'Send message',
        sending: 'Sending...',
        categories: {
          general: 'General inquiry',
          bug: 'Report a bug',
          suggestion: 'Suggestion',
          billing: 'Billing / Payments',
        },
      },
      success: 'Message sent!',
      successDetail: 'We have received your inquiry and will respond to your email soon.',
      supportText: 'Need personalized help?',
    },

    // Errors
    errors: {
      generic: 'An error occurred',
      paymentFailed: 'Payment could not be processed. Please try again or contact support.',
      notFound: 'Not found',
      unauthorized: 'Unauthorized',
      limitReached: 'You have reached your plan limit',
      noRecipes: 'You need to create recipes first',
      rateLimit: 'Too many attempts. For security reasons, your access has been temporarily limited. Please try again in a few minutes.',
      unverified: 'Your account is not verified. Please check your email.',
      codeSent: 'Code sent successfully',
      resendCode: 'Resend code',
    },

    // Success
    success: {
      saved: 'Saved successfully',
      deleted: 'Deleted successfully',
      copied: 'Copied successfully',
    },
  },
};

const I18nContext = createContext();

export function I18nProvider({ children }) {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('language');
    if (saved) return saved;

    // Detect browser language on first visit
    const browserLang = navigator.language.split('-')[0]; // 'en-GB' → 'en'
    return browserLang === 'en' ? 'en' : 'es'; // Support 'en', fallback to 'es'
  });

  useEffect(() => {
    localStorage.setItem('language', language);
    document.documentElement.lang = language;
  }, [language]);

  const t = (key, params = {}) => {
    const keys = key.split('.');
    let value = translations[language];

    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        return key;
      }
    }

    if (Array.isArray(value)) {
      return value.map(item =>
        typeof item === 'string'
          ? item.replace(/\{(\w+)\}/g, (_, param) => params[param] !== undefined ? params[param] : `{${param}}`)
          : item
      );
    }

    if (typeof value === 'string') {
      return value.replace(/\{(\w+)\}/g, (_, param) => params[param] !== undefined ? params[param] : `{${param}}`);
    }

    return value || key;
  };

  return (
    <I18nContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
}

export default I18nContext;
